import {
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type Ref,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { tabbable } from "tabbable";

import css from "./floating-menu.module.scss";

export type FloatingMenuAnchor = DOMRect | { x: number; y: number };
type VerticalPlacement = "below" | "above" | "centered" | "docked";
type VerticalTransform = "0%" | "-100%" | "-50%";

type FloatingMenuProps = {
  anchor: FloatingMenuAnchor;
  offset?: number;
  className?: string;
  dockedClassName?: string;
  children: ReactNode;
  ref?: Ref<HTMLDivElement>;
  onClose?: () => void;
  /** Переворачивать позицию, чтобы уйти от системного меню (тач-планшеты) */
  preferOppositeSide?: boolean;
};

function getAnchorMetrics(anchor: FloatingMenuAnchor) {
  if (anchor instanceof DOMRect) {
    return {
      x: anchor.left + anchor.width / 2,
      top: anchor.top,
      bottom: anchor.bottom,
    };
  }

  return {
    x: anchor.x,
    top: anchor.y,
    bottom: anchor.y,
  };
}

function resolveVerticalPlacement(
  roomAbove: number,
  roomBelow: number,
  menuHeight: number,
  offset: number
): Exclude<VerticalPlacement, "docked"> {
  const fitsBelow = roomBelow >= menuHeight + offset;
  const fitsAbove = roomAbove >= menuHeight + offset;

  if (!fitsBelow && !fitsAbove) return "centered";
  return !fitsBelow && fitsAbove ? "above" : "below";
}

function resolveVerticalPosition(
  placement: Exclude<VerticalPlacement, "docked">,
  anchorTop: number,
  anchorBottom: number,
  offset: number
) {
  if (placement === "centered") {
    return {
      top: (anchorTop + anchorBottom) / 2,
      transformY: "-50%" as VerticalTransform,
    };
  }

  if (placement === "above") {
    return {
      top: anchorTop - offset,
      transformY: "-100%" as VerticalTransform,
    };
  }

  return {
    top: anchorBottom + offset,
    transformY: "0%" as VerticalTransform,
  };
}

function clampVerticalPosition(
  top: number,
  menuHeight: number,
  transformY: VerticalTransform,
  margin: number
) {
  const minTop =
    transformY === "0%"
      ? margin
      : margin + (transformY === "-50%" ? menuHeight / 2 : menuHeight);
  const maxTop =
    transformY === "0%"
      ? innerHeight - margin - menuHeight
      : innerHeight - margin;

  return Math.max(minTop, Math.min(top, maxTop));
}

export function FloatingMenu({
  anchor,
  offset = 0,
  className,
  dockedClassName,
  children,
  ref,
  onClose,
  preferOppositeSide,
}: FloatingMenuProps) {
  const measureRef = useRef<HTMLDivElement>(null);
  const [menuHeight, setMenuHeight] = useState(0);
  const [verticalPlacement, setVerticalPlacement] =
    useState<VerticalPlacement>("below");

  const {
    x: anchorX,
    top: anchorTop,
    bottom: anchorBottom,
  } = getAnchorMetrics(anchor);
  const roomAbove = anchorTop;
  const roomBelow = innerHeight - anchorBottom;

  const recalculate = useEffectEvent(() => {
    const el = measureRef.current;
    if (!el) return;

    const measuredHeight = el.getBoundingClientRect().height;
    setMenuHeight(current =>
      current === measuredHeight ? current : measuredHeight
    );

    if (preferOppositeSide) {
      // На планшете: если не помещается на выбранной стороне — переходим в мобильный режим
      const preferred = roomBelow >= roomAbove ? "above" : "below";
      const spaceNeeded = measuredHeight + offset;
      const shouldDock =
        preferred === "below"
          ? roomBelow < spaceNeeded
          : roomAbove < spaceNeeded;
      setVerticalPlacement(shouldDock ? "docked" : preferred);
      return;
    }

    setVerticalPlacement(
      resolveVerticalPlacement(roomAbove, roomBelow, measuredHeight, offset)
    );
  });

  useLayoutEffect(() => {
    recalculate();
  }, [preferOppositeSide, anchorX, anchorTop, anchorBottom, offset]);

  // При открытии переносим фокус на первый focusable элемент внутри меню,
  // при закрытии — возвращаем туда, откуда пришли.
  useEffect(() => {
    const menu = measureRef.current;
    if (menu) {
      const returnTo = document.activeElement as HTMLElement | null;
      tabbable(menu)[0]?.focus({ preventScroll: true });
      return () => returnTo?.focus({ preventScroll: true });
    }
  }, []);

  // Esc слушаем на самом меню — keydown приходит сюда только когда фокус
  // внутри. Родительский Dialog тоже слушает на panelRef, не на window, —
  // его handler не сработает пока фокус в меню.
  useEffect(() => {
    const menu = measureRef.current;
    if (menu && onClose) {
      function handleKeyDown(e: KeyboardEvent) {
        if (e.key === "Escape") onClose!();
      }
      menu.addEventListener("keydown", handleKeyDown);
      return () => menu.removeEventListener("keydown", handleKeyDown);
    }
  }, [onClose]);

  const isBottomDocked = verticalPlacement === "docked";
  const xAlign =
    anchorX < innerWidth * 0.25
      ? "left"
      : anchorX > innerWidth * 0.75
        ? "right"
        : "center";
  const txOffset =
    xAlign === "left" ? "0%" : xAlign === "right" ? "-100%" : "-50%";

  const anchoredPlacement: Exclude<VerticalPlacement, "docked"> =
    verticalPlacement === "docked" ? "below" : verticalPlacement;
  const { top, transformY } = resolveVerticalPosition(
    anchoredPlacement,
    anchorTop,
    anchorBottom,
    offset
  );

  const margin = 8;
  const clampedTop = clampVerticalPosition(top, menuHeight, transformY, margin);

  return createPortal(
    <>
      {onClose && <div className={css.backdrop} onMouseDown={onClose} />}
      <div
        ref={node => {
          measureRef.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref) ref.current = node;
        }}
        className={clsx(
          css.menu,
          isBottomDocked && css.docked,
          isBottomDocked && dockedClassName,
          className
        )}
        style={
          isBottomDocked
            ? undefined
            : {
                top: clampedTop,
                transform: `translateY(${transformY})`,
                translate: `clamp(${margin}px, calc(${anchorX}px + ${txOffset}), calc(100vw - ${margin}px - 100%)) 0`,
              }
        }
      >
        {children}
      </div>
    </>,
    document.body
  );
}

/** Фиксированное меню, прижатое к низу экрана. Используется на телефонах. */
export function BottomDockedMenu({
  children,
  ref,
  className,
}: {
  children: ReactNode;
  ref?: Ref<HTMLDivElement>;
  className?: string;
}) {
  return createPortal(
    <div ref={ref} className={clsx(css.menu, css.docked, className)}>
      {children}
    </div>,
    document.body
  );
}

export function useFloatingMenu(triggerRef: RefObject<HTMLElement | null>) {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);

  function toggle() {
    if (anchor) {
      setAnchor(null);
    } else if (triggerRef.current) {
      setAnchor(triggerRef.current.getBoundingClientRect());
    }
  }

  function close() {
    setAnchor(null);
  }

  return { anchor, toggle, close };
}
